import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Stack,
  Alert,
  TextField,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import LoadingSpinner from '../../components/Common/LoadingSpinner';
import {
  approvePartnerApplication,
  draftPartnerApplication,
  deletePartnerApplication,
  fetchPartnerApplication,
  rejectPartnerApplication,
} from '../../api/partners';

const PartnerApplicationDetailPage = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decisionNotes, setDecisionNotes] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  const loadApplication = async () => {
    const data = await fetchPartnerApplication(applicationId);
    setApplication(data);
  };

  useEffect(() => {
    let isMounted = true;

    const initialise = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchPartnerApplication(applicationId);
        if (!isMounted) return;
        setApplication(data);
      } catch (err) {
        console.error('Failed to load application', err);
        if (isMounted) {
          setError('Unable to load application. It may have been removed.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initialise();

    return () => {
      isMounted = false;
    };
  }, [applicationId]);

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'campus_micro_factory':
        return 'Campus Micro Factory';
      case 'micro_factory':
        return 'Micro Factory';
      case 'regional_hub':
        return 'Regional Hub';
      case 'specialized_center':
        return 'Specialized Center';
      default:
        return tier;
    }
  };

  const getStatusChipProps = (value) => {
    switch (value) {
      case 'pending_review':
        return { label: 'Pending Review', color: 'warning' };
      case 'approved':
        return { label: 'Approved', color: 'success' };
      case 'rejected':
        return { label: 'Rejected', color: 'error' };
      case 'draft':
        return { label: 'Draft', color: 'info' };
      default:
        return { label: value?.replace('_', ' ') || 'Unknown', color: 'default' };
    }
  };

  const handleAction = async (action) => {
    if (!application) return;

    try {
      setProcessingAction(true);
      const payload = decisionNotes ? { notes: decisionNotes } : {};

      if (action === 'approve') {
        await approvePartnerApplication(applicationId, payload);
        toast.success('Application approved');
        navigate('/partners');
        return;
      }

      if (action === 'reject') {
        await rejectPartnerApplication(applicationId, payload);
        toast.success('Application rejected');
        navigate('/partners');
        return;
      }

      if (action === 'draft') {
        await draftPartnerApplication(applicationId, payload);
        toast.success('Application saved as draft');
        await loadApplication();
        return;
      }
    } catch (err) {
      console.error(`Failed to ${action} application`, err);
      toast.error(err?.response?.data?.error || `Failed to ${action} application`);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this application? This action cannot be undone.')) {
      return;
    }

    try {
      setProcessingAction(true);
      await deletePartnerApplication(applicationId);
      toast.success('Application deleted');
      navigate('/partners');
    } catch (err) {
      console.error('Failed to delete application', err);
      toast.error(err?.response?.data?.error || 'Failed to delete application');
    } finally {
      setProcessingAction(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/partners')} sx={{ mb: 2 }}>
          Back to Applications
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!application) {
    return null;
  }

  const {
    companyName,
    desiredTier,
    status,
    submittedAt,
    capabilities,
    equipmentList,
    certifications,
    references,
    leadTimes,
    qualityMetrics,
    supportedMaterials,
    garmentTypes,
    documents,
    assessment,
  } = application;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/partners')} sx={{ mb: 3 }}>
        Back to Applications
      </Button>

      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Application Review
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review submitted manufacturing partner application details
          </Typography>
        </Box>

        <Stack spacing={2}>
          <TextField
            label="Decision Notes"
            value={decisionNotes}
            onChange={(e) => setDecisionNotes(e.target.value)}
            multiline
            minRows={2}
            placeholder="Add optional notes for audit trail"
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Button
              variant="contained"
              color="success"
              onClick={() => handleAction('approve')}
              disabled={processingAction || status === 'approved'}
            >
              Accept Application
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleAction('reject')}
              disabled={processingAction || status === 'rejected'}
            >
              Deny Application
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleAction('draft')}
              disabled={processingAction || status === 'draft'}
            >
              Save as Draft
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={handleDelete}
              disabled={processingAction}
            >
              Delete
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Company
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {companyName}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Desired Tier
                </Typography>
                <Chip
                  label={getTierLabel(desiredTier)}
                  size="small"
                  sx={{ mt: 1, mb: 2, textTransform: 'capitalize' }}
                />

                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={getStatusChipProps(status).label}
                  color={getStatusChipProps(status).color}
                  size="small"
                  sx={{ mt: 1, mb: 2, textTransform: 'capitalize' }}
                />

                <Typography variant="body2" color="text.secondary">
                  Submitted
                </Typography>
                <Typography variant="body1">
                  {submittedAt ? new Date(submittedAt).toLocaleString() : '—'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Capabilities & Capacity
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Capabilities
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {(capabilities || []).length > 0
                        ? capabilities.map((cap) => (
                            <Chip key={cap} label={cap.replace(/_/g, ' ')} size="small" />
                          ))
                        : <Typography variant="body2">No capabilities listed</Typography>}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Equipment
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {(equipmentList || []).length > 0
                        ? equipmentList.map((item) => <Chip key={item} label={item} size="small" />)
                        : <Typography variant="body2">No equipment listed</Typography>}
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Lead Times
                    </Typography>
                    <List dense>
                      <ListItem disableGutters>
                        <ListItemText primary="Standard" secondary={leadTimes?.standard ? `${leadTimes.standard} days` : '—'} />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemText primary="Rush" secondary={leadTimes?.rush ? `${leadTimes.rush} days` : '—'} />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Quality Metrics
                    </Typography>
                    <List dense>
                      <ListItem disableGutters>
                        <ListItemText
                          primary="Defect Rate"
                          secondary={
                            qualityMetrics?.defectRate !== undefined
                              ? `${qualityMetrics.defectRate}%`
                              : '—'
                          }
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemText
                          primary="On-Time Delivery"
                          secondary={
                            qualityMetrics?.onTimeDelivery !== undefined
                              ? `${qualityMetrics.onTimeDelivery}%`
                              : '—'
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Materials & Garments
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Supported Materials
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: 2 }}>
                  {(supportedMaterials || []).length > 0
                    ? supportedMaterials.map((material) => <Chip key={material} label={material} size="small" />)
                    : <Typography variant="body2">No materials specified</Typography>}
                </Stack>
                <Typography variant="subtitle2" color="text.secondary">
                  Garment Types
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {(garmentTypes || []).length > 0
                    ? garmentTypes.map((type) => <Chip key={type} label={type} size="small" />)
                    : <Typography variant="body2">No garment types specified</Typography>}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Certifications & References
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Certifications
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: 2 }}>
                  {(certifications || []).length > 0
                    ? certifications.map((cert) => <Chip key={cert} label={cert} size="small" />)
                    : <Typography variant="body2">No certifications listed</Typography>}
                </Stack>
                <Typography variant="subtitle2" color="text.secondary">
                  References
                </Typography>
                <List dense>
                  {(references || []).length > 0 ? (
                    references.map((ref, idx) => (
                      <ListItem key={idx} disableGutters>
                        <ListItemText
                          primary={ref.company || ref.name || 'Reference'}
                          secondary={ref.notes || 'No notes provided'}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <Typography variant="body2">No references provided</Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Supporting Documents
            </Typography>
            {(documents || []).length > 0 ? (
              <List dense>
                {documents.map((doc, idx) => (
                  <ListItem key={idx} disableGutters>
                    <ListItemText
                      primary={doc.title || `Document ${idx + 1}`}
                      secondary={doc.url || 'No link provided'}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2">No supporting documents uploaded.</Typography>
            )}
          </CardContent>
        </Card>

        {assessment && Object.keys(assessment).length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Automated Assessment
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Recommendation
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {assessment.recommendation || 'Not available'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Overall Score
                  </Typography>
                  <Typography variant="body1">
                    {assessment.overallScore !== undefined ? assessment.overallScore : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Strengths
                  </Typography>
                  <List dense>
                    {(assessment.strengths || []).length > 0 ? (
                      assessment.strengths.map((item, idx) => (
                        <ListItem key={idx} disableGutters>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))
                    ) : (
                      <Typography variant="body2">No strengths recorded.</Typography>
                    )}
                  </List>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Improvements Needed
                  </Typography>
                  <List dense>
                    {(assessment.improvementsNeeded || []).length > 0 ? (
                      assessment.improvementsNeeded.map((item, idx) => (
                        <ListItem key={idx} disableGutters>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))
                    ) : (
                      <Typography variant="body2">No improvement items recorded.</Typography>
                    )}
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
};

export default PartnerApplicationDetailPage;
